import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CraftedBy from "../../components/CraftedBy";
import AssistantFAB from "../../components/AssistantFAB";
import { api } from "../../lib/api";
import { HC } from "./theme";
import {
  LayoutDashboard, Users, Building2, CalendarCheck, CalendarClock, Plane, Wallet,
  FileBarChart2, Settings as SettingsIcon, Menu as MenuIcon, ChevronDown, ChevronRight,
  ArrowLeft, LogOut, Crown, Bell, HelpCircle, MapPin, Calendar, Headphones, KeyRound,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const NAV = [
  { path: "", label: "Dashboard", Icon: LayoutDashboard },
  { path: "employees", label: "Employees", Icon: Users },
  { path: "departments", label: "Departments", Icon: Building2 },
  { path: "attendance", label: "Attendance", Icon: CalendarCheck },
  { path: "shifts", label: "Shift Scheduling", Icon: CalendarClock },
  { path: "shift-planner", label: "Shift Planner (AI)", Icon: CalendarClock },
  { path: "leaves", label: "Leaves", Icon: Plane },
  { path: "queries", label: "Queries", Icon: HelpCircle },
  { path: "escalations", label: "Escalations", Icon: Bell },
  {
    label: "Payroll", Icon: Wallet, children: [
      { path: "payroll", label: "Payroll Runs" },
      { path: "payslips", label: "Payslips" },
      { path: "payroll-rules", label: "Payroll Rules" },
      { path: "recurring", label: "Recurring Items" },
    ],
  },
  { path: "iam", label: "Identity & Access", Icon: KeyRound },
  { path: "reports", label: "Reports", Icon: FileBarChart2 },
  { path: "settings", label: "Settings", Icon: SettingsIcon },
];

const META = {
  "": { title: null, sub: "Here's what's happening with your team today." },
  employees: { title: "Employees", sub: "Workforce records across all locations." },
  departments: { title: "Departments & Positions", sub: "Structure, reporting lines and headcount." },
  attendance: { title: "Attendance", sub: "Clock-ins, corrections and exceptions." },
  shifts: { title: "Shift Scheduling", sub: "Rosters, conflicts and shift status." },
  "shift-planner": { title: "Intelligent Shift Planner", sub: "Auto-generate a weekly or monthly roster, review, and route through Supervisor + Location Manager approval." },
  leaves: { title: "Leave Management", sub: "Requests, entitlements and balances." },
  payroll: { title: "Payroll Runs", sub: "Calculate, validate, approve and post payroll." },
  payslips: { title: "Payslips", sub: "Generated payslips and PDF downloads." },
  "payroll-rules": { title: "Payroll Rules", sub: "Configurable earnings, tax and pension rules." },
  "queries": { title: "Queries", sub: "Raise and track staff queries with your branch team." },
  "escalations": { title: "Escalation Inbox", sub: "Queries escalated to HR — oldest first, nothing left unanswered." },
  recurring: { title: "Recurring Deductions & Allowances", sub: "Set an item once — it posts automatically every payroll period." },
  iam: { title: "Identity & Access Management", sub: "Provision, reassign, reset — every login in one place." },
  reports: { title: "HR Reports", sub: "Headcount · attendance · payroll · leave · overtime." },
  settings: { title: "HR Settings", sub: "Shift types · leave types · audit · AI usage." },
};

export default function HRLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selLoc, setSelLoc] = useState(user?.location_id || "all");
  const [now, setNow] = useState(new Date());
  const [pending, setPending] = useState(0);

  useEffect(() => {
    api.get("/locations").then(setLocations).catch(() => {});
    api.get("/hr/dashboard").then((d) => setPending((d?.pending_leave_count || 0) + (d?.current_payroll?.exception_count || 0) + (d?.current_payroll?.status === "pending_approval" ? 1 : 0))).catch(() => {});
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);

  const path = loc.pathname.replace(/^\/hr\/?/, "");
  const meta = META[path] || META[""];
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = (user?.name || "there").split(" ")[0];
  const scoped = !!user?.location_id && !user?.is_admin;
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const locName = scoped
    ? locations.find((l) => l.id === user.location_id)?.name || "My location"
    : selLoc === "all" ? "All Locations" : locations.find((l) => l.id === selLoc)?.name || "Location";
  const dateStr = now.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold transition-colors ${isActive ? "" : "hover:bg-[#FBF8F2]"}`;
  const navStyle = ({ isActive }) => ({
    background: isActive ? HC.burgundySoft : "transparent",
    color: isActive ? HC.burgundy : HC.text,
    borderLeft: isActive ? `3px solid ${HC.gold}` : "3px solid transparent",
  });

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: HC.canvas }}>
      <aside data-testid="hr-sidebar"
        className={`w-[248px] ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-full flex flex-col transition-transform duration-300`}
        style={{ background: HC.panel, borderRight: `1px solid ${HC.border}` }}>
        <div className="h-[84px] flex items-center gap-3 px-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: HC.burgundy }}>
            <Crown size={21} color={HC.gold} strokeWidth={2.2} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-brand font-bold text-[26px] tracking-tight" style={{ color: HC.burgundy }}>Sir Monty</div>
            <div className="text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.soft }}>ERP</div>
          </div>
        </div>

        <div className="px-5 pt-2 pb-3 text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.gold }}>HR &amp; PAYROLL</div>

        <nav className="flex-1 overflow-y-auto px-3 space-y-1">
          {NAV.map((item) => item.children ? (
            <div key={item.label}>
              <button data-testid="hr-nav-payroll-group" onClick={() => setPayrollOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold hover:bg-[#FBF8F2]"
                style={{ color: HC.text, borderLeft: "3px solid transparent" }}>
                <item.Icon size={18} />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown size={15} className={`transition-transform ${payrollOpen ? "" : "-rotate-90"}`} style={{ color: HC.soft }} />
              </button>
              {payrollOpen && item.children.map((c) => (
                <NavLink key={c.path} to={`/hr/${c.path}`} onClick={() => setMobileOpen(false)}
                  data-testid={`hr-nav-${c.path}`}
                  className={({ isActive }) => `flex items-center gap-2 pl-12 pr-3 h-10 rounded-xl text-[13px] font-medium ${isActive ? "" : "hover:bg-[#FBF8F2]"}`}
                  style={({ isActive }) => ({ background: isActive ? HC.burgundySoft : "transparent", color: isActive ? HC.burgundy : HC.muted })}>
                  {c.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <NavLink key={item.path} to={item.path === "" ? "/hr" : `/hr/${item.path}`} end={item.path === ""}
              onClick={() => setMobileOpen(false)} data-testid={`hr-nav-${item.path || "dashboard"}`}
              className={navClass} style={navStyle}>
              <item.Icon size={18} className="shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mx-4 my-4 rounded-2xl border flex items-center gap-3 px-4 h-14"
          style={{ borderColor: HC.border, background: HC.panelSoft }}>
          <Headphones size={17} style={{ color: HC.burgundy }} />
          <span className="text-sm font-semibold" style={{ color: HC.text }}>Need Help?</span>
        </div>

        <div className="px-5 pb-4 flex items-center gap-3">
          <Crown size={22} style={{ color: HC.gold }} />
          <div className="font-brand italic leading-tight" style={{ color: HC.burgundy }}>
            <div className="text-[13px] font-semibold">Great Food,</div>
            <div className="text-[13px] font-semibold">Great Moments.</div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center gap-2" style={{ borderColor: HC.border }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
            style={{ background: HC.borderSoft, color: HC.muted }}>{initials}</div>
          <div className="leading-tight">
            <div className="text-[10px]" style={{ color: HC.soft }}>Your role:</div>
            <div className="text-[12px] font-semibold" style={{ color: HC.text }}>{user?.role?.name || "User"}</div>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="shrink-0 h-[84px] flex items-center gap-3 px-4 lg:px-7 border-b"
          style={{ borderColor: HC.border, background: HC.canvas }}>
          <button className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center" onClick={() => setMobileOpen(true)} style={{ color: HC.text }}>
            <MenuIcon size={20} />
          </button>
          <div className="min-w-0">
            <div className="font-brand font-bold text-[22px] lg:text-[26px] leading-tight truncate" style={{ color: HC.text }} data-testid="hr-page-title">
              {meta.title || <>{greeting}, {first} <span>👋</span></>}
            </div>
            <div className="text-[12px] lg:text-[13px] truncate" style={{ color: HC.soft }}>{meta.sub}</div>
          </div>

          <div className="flex-1" />

          <div className="hidden lg:flex items-center gap-2 h-11 px-3.5 rounded-xl border bg-white" style={{ borderColor: HC.border }}>
            <MapPin size={15} style={{ color: HC.muted }} />
            {scoped ? (
              <span className="text-sm font-semibold" style={{ color: HC.text }}>{locName}</span>
            ) : (
              <select data-testid="hr-location-filter" value={selLoc} onChange={(e) => setSelLoc(e.target.value)}
                className="text-sm font-semibold bg-transparent outline-none pr-1" style={{ color: HC.text }}>
                <option value="all">All Locations</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>

          <div className="hidden xl:flex items-center gap-2 h-11 px-3.5 rounded-xl border bg-white" style={{ borderColor: HC.border }}>
            <Calendar size={15} style={{ color: HC.muted }} />
            <span className="text-sm font-semibold tabular-nums" style={{ color: HC.text }}>Today, {dateStr}</span>
            <ChevronDown size={14} style={{ color: HC.soft }} />
          </div>

          <button data-testid="hr-bell" className="relative w-10 h-10 rounded-full flex items-center justify-center border bg-white"
            style={{ borderColor: HC.border, color: HC.text }}>
            <Bell size={17} />
            {pending > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 text-white"
                style={{ background: HC.red }}>{pending}</span>
            )}
          </button>
          <button className="hidden md:flex w-10 h-10 rounded-full items-center justify-center border bg-white"
            style={{ borderColor: HC.border, color: HC.text }}><HelpCircle size={17} /></button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5" data-testid="hr-user-menu">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: HC.borderSoft, color: HC.muted }}>{initials}</div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold" style={{ color: HC.text }}>{first}</div>
                  <div className="text-[11px]" style={{ color: HC.soft }}>{user?.role?.name || "User"}</div>
                </div>
                <ChevronDown size={14} style={{ color: HC.soft }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs font-normal" style={{ color: HC.muted }}>{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/")}><ArrowLeft size={15} className="mr-2" /> Back to ERP</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} className="text-[#A52222]">
                <LogOut size={15} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="hr-main">
          <div className="p-4 lg:p-6 max-w-[1680px] mx-auto w-full">
            <Outlet context={{ locationId: scoped ? user.location_id : selLoc === "all" ? null : selLoc, locations }} />
          </div>
          <footer className="px-4 lg:px-8 pb-4 pt-1 flex items-center justify-between text-[11px]" style={{ color: "#A58F7C" }}>
            <div>© {new Date().getFullYear()} Sir Monty Enterprise ERP · HR &amp; Payroll</div>
            <CraftedBy />
          </footer>
          <AssistantFAB />
        </main>
      </div>
    </div>
  );
}
